import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Button } from '@/components/Button';
import { useAlertsStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { IssueMarkdown } from '@/tracker/entities/issue';
import { useTrackerStore } from '@/tracker/model';
import { Loader } from '@/tracker/ui/Loader';

import { CardSection } from '../CardSection';
import { CommentList } from '../CommentList';
import { EditableBlock } from '../EditableBlock';
import { IssueCardAside } from '../IssueCardAside';
import { IssueCardHeader } from '../IssueCardHeader';
import { IssueLinks } from '../IssueLinks';
import { IssueSubtasks } from '../IssueSubtasks';
import { TextEditor } from '../TextEditor';
import { IssueCardStore } from '../../model/IssueCardStore';

import s from './IssueCard.module.scss';

type Props = {
  issueKey: string;
  onClose: () => void;
};

/** Карточка задачи: название, описание, подзадачи и комментарии слева, поля справа */
const IssueCard: React.FC<Props> = observer(({ issueKey, onClose }) => {
  const trackerStore = useTrackerStore();
  const alertsStore = useAlertsStore();
  const { store } = useLocalStore(
    () => new IssueCardStore({ key: issueKey, trackerStore, alertsStore }),
  );

  useEffect(() => {
    store.load();
  }, [store]);

  if (store.isLoading && !store.issue) return <Loader />;

  if (!store.issue) {
    return (
      <div className={s.card}>
        <IssueCardHeader issueKey={issueKey} queue={null} onClose={onClose} />
        <div className={s.card__state}>
          <Typography color="text.secondary">
            {store.isNotFound
              ? `Задача ${issueKey} не найдена или нет доступа`
              : 'Не удалось загрузить задачу'}
          </Typography>
          {!store.isNotFound && <Button onClick={store.load}>Повторить</Button>}
        </div>
      </div>
    );
  }

  const { issue } = store;
  const isLocked = store.isSaving || store.isReadOnly;

  return (
    <article className={s.card} aria-label={`Задача ${issue.key}`}>
      <IssueCardHeader issueKey={issue.key} queue={issue.queue} onClose={onClose} />

      <div className={s.card__body}>
        <div className={s.card__main}>
          <div className={s.card__title}>
            <EditableBlock
              label="название"
              disabled={isLocked}
              renderEditor={(close) => (
                <TextEditor
                  initialValue={issue.summary}
                  ariaLabel="Название задачи"
                  validate={(text) => (text.trim() ? null : 'Название не может быть пустым')}
                  onSave={(text) => store.update({ summary: text.trim() })}
                  onClose={close}
                />
              )}
            >
              <h2 className={s.card__titleText}>{issue.summary}</h2>
            </EditableBlock>
          </div>

          <CardSection title="Описание">
            <EditableBlock
              label="описание"
              disabled={isLocked}
              renderEditor={(close) => (
                <TextEditor
                  multiline
                  initialValue={issue.description ?? ''}
                  ariaLabel="Описание задачи"
                  placeholder="Разметка YFM: **жирный**, списки, ссылки"
                  onSave={(text) => store.update({ description: text })}
                  onClose={close}
                />
              )}
            >
              <IssueMarkdown text={issue.description ?? ''} emptyText="Добавить описание" />
            </EditableBlock>
          </CardSection>

          <IssueSubtasks issue={issue} store={store} />

          <IssueLinks issue={issue} store={store} />

          <CardSection title="Комментарии" count={store.comments.length}>
            <CommentList store={store} />
          </CardSection>
        </div>

        <IssueCardAside issue={issue} store={store} />
      </div>
    </article>
  );
});

export { IssueCard };
